// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Spin from 'antd/lib/spin';
import Result from 'antd/lib/result';

import getCore from 'cvat-core-wrapper';

const core = getCore();

interface ParamType {
    certId: string;
}

type LookupState = 'loading' | 'notfound' | 'error';

export default function CertificateRedirectPageComponent(): JSX.Element {
    const { certId } = useParams<ParamType>();
    const history = useHistory();
    const [status, setStatus] = useState<LookupState>('loading');

    useEffect(() => {
        let aborted = false;
        setStatus('loading');

        async function resolveCertificate(): Promise<void> {
            try {
                const response = await core.server.request(`${core.config.backendAPI}/task-url`, {
                    method: 'POST',
                    headers: {
                        'Content-type': 'application/json',
                    },
                    data: JSON.stringify({ certificate_ids: certId }),
                });

                if (aborted) {
                    return;
                }

                const match = response?.results?.[certId];
                if (match) {
                    history.replace(`/tasks/${match.task_id}/jobs/${match.job_id}?frame=${match.frame}`);
                } else {
                    setStatus('notfound');
                }
            } catch (error) {
                if (!aborted) {
                    setStatus('error');
                }
            }
        }

        resolveCertificate();

        return () => {
            aborted = true;
        };
    }, [certId]);

    if (status === 'notfound') {
        return (
            <Result
                className='cvat-not-found'
                status='404'
                title={`Sorry, but certificate ${certId} was not found`}
                subTitle='Please, be sure the certificate id you requested exists and you have appropriate permissions'
            />
        );
    }

    if (status === 'error') {
        return (
            <Result
                className='cvat-not-found'
                status='error'
                title={`Could not resolve certificate ${certId}`}
                subTitle='An unexpected error occurred while looking up the certificate. Please try again later.'
            />
        );
    }

    return <Spin size='large' className='cvat-spinner' />;
}
